import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useStyles = makeStyles({
  historyList: {
    maxWidth: "900px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[4],
    "@media (max-width: 480px)": {
      gap: designTokens.spacing[3],
    },
  },

  noMessagesContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: designTokens.spacing[10],
    color: designTokens.neutral[400],
    fontStyle: "italic",
    fontSize: designTokens.fontSize.sm,
  },

  sectionDivider: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    marginBottom: designTokens.spacing[2],
  },

  sectionDividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: designTokens.neutral[200],
  },

  sectionDividerChip: {
    backgroundColor: designTokens.primary[800],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.full,
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    fontFamily: designTokens.fontFamily,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  messageBlock: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    "@media (max-width: 480px)": {
      gap: designTokens.spacing[2],
    },
  },

  aiBubbleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: designTokens.spacing[2],
  },

  aiAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.primary[900],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: designTokens.primary[300],
    "@media (max-width: 480px)": {
      display: "none",
    },
  },

  aiBubble: {
    position: "relative",
    backgroundColor: designTokens.neutral[100],
    borderRadius: `0 ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg}`,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    maxWidth: "75%",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[800],
    lineHeight: designTokens.lineHeight.relaxed,
    boxShadow: designTokens.shadow.sm,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    "::before": {
      content: '""',
      display: "none",
      position: "absolute",
      top: "12px",
      left: "-7px",
      width: "0",
      height: "0",
      borderTop: "6px solid transparent",
      borderBottom: "6px solid transparent",
      borderRight: `7px solid ${designTokens.neutral[100]}`,
    },
    "@media (max-width: 480px)": {
      maxWidth: "88%",
      "::before": {
        display: "block",
      },
    },
  },

  currentQuestionBubble: {
    backgroundColor: designTokens.accent[50],
    border: `1px solid ${designTokens.accent[200]}`,
    borderRadius: `0 ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg}`,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    maxWidth: "75%",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[800],
    lineHeight: designTokens.lineHeight.relaxed,
    boxShadow: designTokens.shadow.sm,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    "@media (max-width: 480px)": {
      maxWidth: "88%",
    },
  },

  userBubbleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: designTokens.spacing[2],
  },

  userAvatar: {
    width: "28px",
    height: "28px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.primary[100],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: designTokens.primary[600],
    "@media (max-width: 480px)": {
      display: "none",
    },
  },

  userBubble: {
    position: "relative",
    backgroundColor: designTokens.primary[50],
    border: `1px solid ${designTokens.primary[100]}`,
    borderRadius: `${designTokens.borderRadius.lg} 0 ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg}`,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    maxWidth: "70%",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.primary[800],
    lineHeight: designTokens.lineHeight.relaxed,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    "::before": {
      content: '""',
      display: "none",
      position: "absolute",
      top: "12px",
      right: "-7px",
      width: "0",
      height: "0",
      borderTop: "6px solid transparent",
      borderBottom: "6px solid transparent",
      borderLeft: `7px solid ${designTokens.primary[50]}`,
    },
    "@media (max-width: 480px)": {
      maxWidth: "88%",
      "::before": {
        display: "block",
      },
    },
  },

  typingBubble: {
    position: "relative",
    backgroundColor: designTokens.neutral[100],
    borderRadius: `0 ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg} ${designTokens.borderRadius.lg}`,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    boxShadow: designTokens.shadow.sm,
    "::before": {
      content: '""',
      display: "none",
      position: "absolute",
      top: "12px",
      left: "-7px",
      width: "0",
      height: "0",
      borderTop: "6px solid transparent",
      borderBottom: "6px solid transparent",
      borderRight: `7px solid ${designTokens.neutral[100]}`,
    },
    "@media (max-width: 480px)": {
      "::before": {
        display: "block",
      },
    },
  },

  typingDot: {
    width: "8px",
    height: "8px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.neutral[400],
    flexShrink: 0,
    animationName: {
      "0%, 60%, 100%": { transform: "translateY(0)" },
      "30%": { transform: "translateY(-6px)" },
    },
    animationDuration: "1.2s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },

  typingDot2: {
    animationDelay: "0.2s",
  },

  typingDot3: {
    animationDelay: "0.4s",
  },

  icon20: {
    width: "20px",
    height: "20px",
  },
});
